import { Wand } from "lucide-react";
import { Link } from "react-router-dom";
import { getLogoUrl } from "@/utils/assets";
import { useAuth } from "@/context/AuthContext";

interface LogoProps {
  className?: string;
  mini?: boolean;
  linkToHome?: boolean;
  showText?: boolean;
  useImageLogo?: boolean;
}

/**
 * Logo Image Content.
 */
function LogoImageContent({
  className,
  mini,
  showText,
}: Readonly<Pick<LogoProps, "className" | "mini" | "showText">>) {
  return (
    <div className={`flex items-center gap-2 ${className} ${mini ? "min-w-0" : ""}`}>
      <img
        src={getLogoUrl("clean")}
        alt="TellerWand - Personal Finance Management Platform Logo"
        className={`${mini ? "w-8 h-8" : "w-10 h-10"} object-contain rounded-md flex-shrink-0`}
        width={mini ? 32 : 40}
        height={mini ? 32 : 40}
      />
      {showText && !mini && <span className="font-bold text-lg truncate">TellerWand</span>}
    </div>
  );
}

/**
 * Logo Icon Content.
 */
function LogoIconContent({
  className,
  mini,
  showText,
}: Readonly<Pick<LogoProps, "className" | "mini" | "showText">>) {
  return (
    <div className={`flex items-center gap-2 ${className} ${mini ? "min-w-0" : ""}`}>
      <Wand className={`${mini ? "w-5 h-5" : "w-6 h-6"} text-primary flex-shrink-0`} />
      {showText && !mini && <span className="font-bold text-lg truncate">TellerWand</span>}
    </div>
  );
}

/**
 * Renders the logo of the application with optional text and link settings.
 */
export const Logo = ({
  className = "",
  mini = false,
  linkToHome = true,
  showText = true,
  useImageLogo = false,
}: LogoProps) => {
  const { user } = useAuth();

  const logoContent = useImageLogo ? (
    <LogoImageContent className={className} mini={mini} showText={showText} />
  ) : (
    <LogoIconContent className={className} mini={mini} showText={showText} />
  );

  if (!linkToHome) {
    return logoContent;
  }

  const linkTo = user ? "/dashboard" : "/";
  return (
    <Link to={linkTo} className="hover:opacity-80 transition-opacity" aria-label="TellerWand home">
      {logoContent}
    </Link>
  );
};
